<script setup lang="ts">
import { computed, ComputedRef, onBeforeMount, ref, watch } from "vue";
import { usePlaceStore } from "@/modules/place-module";
import { UiButton, UiTabs } from "@packages/component-library";
import PlaceSlider from "./PlaceSlider.vue";
import PlaceReview from "./PlaceReview.vue";
import PlaceMenu from "../PlaceMenu/PlaceMenu.vue";
import { useRoute, useRouter } from "vue-router";
import PlaceReviews from "../PlaceReviews/PlaceReviews.vue";
import PlaceBadges from "./PlaceBadges.vue";
import { onClickOutside, useWindowSize } from "@vueuse/core";
import { IUser, useUserStore } from "@/modules/user-module";
import AppTooltip from "@/components/AppTooltip.vue";
import PlaceFeedbackContacts from "./PlaceFeedbackContacts.vue";
import ShowMoreText from "@/components/ShowMoreText.vue";
import {
  IconBagRegular,
  IconChevronDoubleRightRegular,
  IconHeartFill,
  IconHeartRegular,
  IconLinkMobileRegular,
} from "@packages/icon-library";
import { useCartStore } from "@/modules/cart-module";
import { useI18n } from "vue-i18n";

const emit = defineEmits([
  "ratePlace",
  "showPaymentModal",
  "showYandexTermsModal",
  "showCashbackModal",
  "favoriteUpdate",
  "showFeedbackContacts",
]);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const placeStore = usePlaceStore();
const userStore = useUserStore();
const cartStore = useCartStore();
const { width } = useWindowSize();
const place = computed(() => placeStore.place);
const user: ComputedRef<null | IUser> = computed(() => userStore.user);
const favoriteLoader = ref(false);
const showFeedbackContacts = ref(false);
const tabs = ref([{ id: "info", title: t("place.info.review") }]);
const selectTab = ref("info");
const target: null | HTMLElement = ref(null);

const changeFavoriteStatus = () => {
  if (!favoriteLoader.value) {
    favoriteLoader.value = true;

    if (place.value?.place.is_favorite) {
      placeStore
        .setPlaceUnFavorite(route.query.place)
        .then(() => {
          emit("favoriteUpdate");
        })
        .finally(() => {
          favoriteLoader.value = false;
        });
      return;
    }

    placeStore
      .setPlaceFavorite(route.query.place)
      .then(() => {
        emit("favoriteUpdate");
      })
      .finally(() => {
        favoriteLoader.value = false;
      });
  }
};

const showPlaceFeedbackContacts = () => {
  if (showFeedbackContacts.value) {
    showFeedbackContacts.value = false;
    return;
  }

  if (width.value > 767) {
    showFeedbackContacts.value = true;
    return;
  }

  emit("showFeedbackContacts");
};

const sharePlaceLink = () => {
  navigator.share({
    title: place.value?.place.title,
    text: `${place.value?.place?.frontend_url}`,
  });
};

onClickOutside(target, () => {
  showFeedbackContacts.value = false;
});

watch(selectTab, () => {
  if (selectTab.value === "info") {
    router.replace({ query: { place: route.query.place } });
    return;
  }

  router.replace({ query: { place: route.query.place, tab: selectTab.value } });
});

onBeforeMount(() => {
  if (place.value?.products.length) {
    tabs.value.push({ id: "menu", title: t("place.info.menu") });

    if (route.query.tab === "menu") {
      selectTab.value = "menu";
    }
  }
  if (place.value?.reviews.length) {
    tabs.value.push({ id: "reviews", title: t("place.info.reviews") });

    if (route.query.tab === "reviews") {
      selectTab.value = "reviews";
    }
  }
});
</script>

<template>
  <div class="place-aside">
    <header class="place-header">
      <RouterLink :to="{ name: route.name }">
        <IconChevronDoubleRightRegular />
      </RouterLink>

      <div class="place-header__actions">
        <button
          v-if="user"
          type="button"
          :disabled="favoriteLoader"
          @click="changeFavoriteStatus"
        >
          <IconHeartFill v-if="place?.place.is_favorite" />
          <IconHeartRegular v-else />
        </button>
        <button v-if="width < 1240" type="button" @click="sharePlaceLink">
          <IconLinkMobileRegular />
        </button>
        <RouterLink v-if="user" :to="{ name: 'CartView' }">
          <span v-if="cartStore.getCartProductsLength">
            {{
              cartStore.getCartProductsLength <= 9
                ? cartStore.getCartProductsLength
                : "9+"
            }}
          </span>
          <IconBagRegular />
        </RouterLink>
      </div>
    </header>
    <section class="place-info">
      <PlaceSlider />

      <div class="place-info__description">
        <h3>{{ place?.place.title }}</h3>
        <p>
          <span
            :class="`${place?.place.working_status.is_closed ? 'closed' : 'open'}`"
          >
            {{ place?.place.working_status.status }}
          </span>
          •
          <template v-if="place?.place.badges.includes('online')">
            {{ t("place.info.online") }} •
          </template>
          {{ place?.place.category_title }}
        </p>
        <PlaceBadges
          @showPaymentModal="emit('showPaymentModal')"
          @showYandexTermModal="emit('showYandexTermsModal')"
          @showCashbackModal="emit('showCashbackModal')"
        />
        <ShowMoreText :text="place?.place.description_html" />
      </div>

      <div class="place-info__tabs">
        <UiTabs v-model="selectTab" size="m" :tabs="tabs" />
      </div>

      <PlaceReview
        v-if="selectTab === 'info'"
        @showReviews="selectTab = 'reviews'"
        @ratePlace="emit('ratePlace', $event)"
      />

      <KeepAlive>
        <PlaceMenu v-if="selectTab === 'menu'" />
      </KeepAlive>

      <KeepAlive>
        <PlaceReviews
          v-if="selectTab === 'reviews'"
          @ratePlace="emit('ratePlace', $event)"
        />
      </KeepAlive>
    </section>
    <section class="place-info__actions">
      <div ref="target">
        <AppTooltip v-if="showFeedbackContacts && width > 767">
          <PlaceFeedbackContacts />
        </AppTooltip>
        <UiButton
          :label="t('place.info.contact')"
          size="m"
          :disabled="!place?.place.feedback_contacts.length"
          data-test-id="place-button-feedbackContacts"
          @click="showPlaceFeedbackContacts"
        />
      </div>
    </section>
  </div>
</template>

<style scoped lang="scss">
.place-aside {
  height: 100%;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr auto;
  align-items: start;
}

.place-info {
  overflow: auto;
  overscroll-behavior: contain;
  height: 100%;

  &__description {
    padding: calc($base-space * 6) calc($base-space * 8);

    @media (max-width: 1023px) {
      padding: calc($base-space * 5) calc($base-space * 6) calc($base-space * 6);
    }

    @media (max-width: 767px) {
      padding: calc($base-space * 4) calc($base-space * 4) calc($base-space * 5);
    }

    > h3 {
      color: var(--color-semantic-content-normal-primary);
      @include h4();
    }

    > p {
      margin: calc($base-space * 2) 0 0;
      @include body-m();
      color: var(--color-semantic-content-normal-tertiary);
      gap: calc($base-space * 2);

      @media (max-width: 1023px) {
        margin: calc($base-space * 1.5) 0 0;
      }

      > span {
        &.closed {
          color: var(--color-semantic-content-normal-negative);
        }

        &.open {
          color: var(--color-semantic-content-normal-positive);
        }
      }
    }

    > .show-more-text {
      margin: calc($base-space * 4) 0 0;
    }
  }

  &__tabs {
    padding: 0 calc($base-space * 8);
    position: relative;

    @media (max-width: 1023px) {
      padding: 0 calc($base-space * 6);
    }

    @media (max-width: 767px) {
      padding: 0 calc($base-space * 4);
    }

    &:deep(.ui-tabs) {
      padding: 0;
    }

    &:after {
      content: "";
      width: 100%;
      height: 1px;
      background-color: var(--color-semantic-border-normal-soft);
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
    }

    &:deep(.tab) {
      z-index: 2;
    }
  }

  &__actions {
    padding: calc($base-space * 4) calc($base-space * 8) calc($base-space * 8);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: calc($base-space * 3);

    @media (max-width: 1023px) {
      padding: calc($base-space * 3) calc($base-space * 6) calc($base-space * 6);
    }

    @media (max-width: 767px) {
      padding: calc($base-space * 3) calc($base-space * 4) calc($base-space * 4);
      gap: calc($base-space * 2);
    }

    > div {
      width: 100%;
      position: relative;

      > button {
        width: 100%;
        @media (max-width: 1023px) {
          height: 44px;
        }
      }
    }
  }
}

.place-header {
  padding: calc($base-space * 2) calc($base-space * 4);
  display: flex;
  align-items: center;
  justify-content: space-between;

  > a {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;

    > .icon {
      background-color: var(--color-semantic-content-normal-primary);
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: $base-space;

    > button {
      width: 48px;
      height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;

      @media (max-width: 1023px) {
        width: 40px;
        height: 40px;
      }

      > .icon {
        background-color: var(--color-semantic-content-normal-primary);

        @media (max-width: 1023px) {
          width: 20px;
          height: 20px;
        }

        &.icon-favorite-fill {
          background-color: var(--color-non-semantic-background-red);
        }
      }
    }

    > a {
      width: 48px;
      height: 48px;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;

      @media (max-width: 1023px) {
        width: 40px;
        height: 40px;
      }

      > .icon {
        background-color: var(--color-semantic-content-normal-primary);

        @media (max-width: 1023px) {
          width: 20px;
          height: 20px;
        }
      }

      > span:not(.icon) {
        width: 16px;
        height: 16px;
        @include caption();
        border-radius: 50%;
        background-color: var(--color-semantic-background-normal-brand);
        color: var(--color-semantic-content-normal-static-dark);
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        z-index: 2;
        top: 0;
        right: 0;
      }
    }
  }
}
</style>
